import React, { useEffect, useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import { getProjectLeave } from "../../config/services/employeeServices";
import "./ProjectLeaveCalendarView.css";

// Helper function to get color based on leave type
const getLeaveColor = (leaveType) => {
    const colors = {
        1: "#E91E63",  // CL - Pink
        2: "#2196F3",  // EL - Blue
        3: "#FF9800",  // SL - Orange
        4: "#F44336",  // LWP - Red
        5: "#9C27B0",  // Compensatory Off - Purple
        6: "#E91E63",  // Half CL - Pink
        7: "#2196F3",  // Half EL - Blue
        8: "#FF9800",  // Half SL - Orange
        9: "#F44336",  // Half LWP - Red
        10: "#9C27B0", // Half Comp Off - Purple
        11: "#00BCD4",  // WFH - Cyan
        12: "#FFC107",  // Short Leave - Amber
        13: "#4CAF50",  // Full Others - Green
        14: "#4CAF50",  // Half Others - Green
        15: "#00BCD4",  // Half WFH - Cyan
        16: "#673AB7",  // Client Off - Deep Purple
        17: "#795548",  // Paternal Leave - Brown
        18: "#607D8B",  // Bereavement Leave - Blue Grey
    };

    return colors[leaveType] || "#607D8B";
};

// Helper function to get leave type name
const getLeaveTypeName = (leaveType) => {
    const types = {
        1: "CL",
        2: "EL",
        3: "SL",
        4: "LWP",
        5: "Comp Off",
        6: "Half CL",
        7: "Half EL",
        8: "Half SL",
        9: "Half LWP",
        10: "Half Comp Off",
        11: "WFH",
        12: "Short Leave",
        13: "Others",
        14: "Half Others",
        15: "Half WFH",
        16: "Client Off",
        17: "Paternal Leave",
        18: "Bereavement Leave",
    };
    return types[leaveType] || "Leave";
};

// Helper function to strip HTML tags from text
const stripHtmlTags = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
};

// Transform project leave data to FullCalendar event format
const transformLeaveToEvent = (leave) => {
    const { _id, empName, leaveType, startDate, endDate } = leave;

    let title = `${empName} - ${getLeaveTypeName(leaveType)}`;

    return {
        id: _id,
        title: title,
        start: moment(startDate).format("YYYY-MM-DD"),
        end: moment(endDate).add(1, 'day').format("YYYY-MM-DD"), // FullCalendar end is exclusive
        backgroundColor: getLeaveColor(leaveType),
        borderColor: getLeaveColor(leaveType),
        extendedProps: {
            ...leave,
        },
    };
};

function ProjectLeaveCalendarView({ projectMemberLeave = [], projectId }) {
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [viewLeaveModal, setViewLeaveModal] = useState(false);
    const [loadedMonths, setLoadedMonths] = useState(new Set());
    const [allLeaves, setAllLeaves] = useState([]);
    const [loading, setLoading] = useState(false);

    // Get month key for caching (YYYY-MM format)
    const getMonthKey = (date) => {
        return moment(date).format("YYYY-MM");
    };

    // Fetch leaves for a specific month
    const fetchLeavesForMonth = async (date, projectId) => {
        const monthKey = getMonthKey(date);

        // Check if already loaded
        if (loadedMonths.has(monthKey)) {
            return;
        }

        setLoading(true);

        try {
            const params = {
                projectId: projectId,
                month: moment(date).month() + 1,
                year: moment(date).year()
            };

            const response = await getProjectLeave(params);

            if (response.data.statusCode === 1 && response.data.responseData?.leaveList?.result) {
                const newLeaves = response.data.responseData.leaveList.result;
                console.log(`[ProjectLeaveCalendar] Fetched ${newLeaves.length} leaves for ${monthKey}`);

                // Deduplicate before adding to prevent duplicates from multi-month leaves
                setAllLeaves(prev => {
                    const existingIds = new Set(prev.map(leave => leave._id));
                    const uniqueNewLeaves = newLeaves.filter(leave => !existingIds.has(leave._id));
                    const duplicatesSkipped = newLeaves.length - uniqueNewLeaves.length;
                    if (duplicatesSkipped > 0) {
                        console.log(`[ProjectLeaveCalendar] Skipped ${duplicatesSkipped} duplicate(s) for ${monthKey}`);
                    }
                    console.log(`[ProjectLeaveCalendar] Adding ${uniqueNewLeaves.length} unique leaves. Total: ${prev.length + uniqueNewLeaves.length}`);
                    return [...prev, ...uniqueNewLeaves];
                });
                setLoadedMonths(prev => new Set([...prev, monthKey]));
            }
        } catch (error) {
            console.error("Error fetching leaves:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initialize with current month data from props - use useMemo to prevent duplicates
    const initialLeaves = useMemo(() => {
        if (projectMemberLeave && projectMemberLeave.length > 0) {
            console.log('[ProjectLeaveCalendar] Initial leaves received:', projectMemberLeave.length);
            // Deduplicate based on _id to prevent duplicates from backend
            const uniqueLeaves = [];
            const seenIds = new Set();
            for (const leave of projectMemberLeave) {
                if (!seenIds.has(leave._id)) {
                    seenIds.add(leave._id);
                    uniqueLeaves.push(leave);
                }
            }
            const duplicatesRemoved = projectMemberLeave.length - uniqueLeaves.length;
            if (duplicatesRemoved > 0) {
                console.log('[ProjectLeaveCalendar] Duplicates removed from initial load:', duplicatesRemoved);
            }
            console.log('[ProjectLeaveCalendar] Unique leaves after deduplication:', uniqueLeaves.length);
            return uniqueLeaves;
        }
        return [];
    }, [projectMemberLeave]);

    useEffect(() => {
        if (initialLeaves.length > 0) {
            const currentMonthKey = getMonthKey(new Date());
            // Only initialize if not already loaded
            if (!loadedMonths.has(currentMonthKey)) {
                setAllLeaves(initialLeaves);
                setLoadedMonths(new Set([currentMonthKey]));
            }
        }
    }, [initialLeaves]);

    // Transform leaves to events - only use allLeaves to avoid duplicates
    const leaveEvents = allLeaves.map(transformLeaveToEvent);

    // Handle event click
    const handleEventClick = (clickInfo) => {
        const leave = clickInfo.event.extendedProps;
        setSelectedLeave(leave);
        setViewLeaveModal(true);
    };

    // Close modal
    const closeModal = () => {
        setViewLeaveModal(false);
        setSelectedLeave(null);
    };

    // Handle month navigation
    const handleDatesSet = (dateInfo) => {
        const newDate = dateInfo.view.currentStart;

        // Fetch current month if not loaded
        if (projectId) {
            fetchLeavesForMonth(newDate, projectId);
        }
    };

    return (
        <div className="leave-calendar-container">
            {loading && (
                <div style={{ textAlign: 'center', padding: '10px', color: '#1890ff' }}>
                    <span>Loading leaves...</span>
                </div>
            )}
            <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "",
                }}
                events={leaveEvents}
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                height="auto"
                eventDisplay="block"
                dayMaxEvents={3}
                moreLinkText={(num) => `+${num} more`}
                showNonCurrentDates={false}
            />

            {/* Leave Detail Modal */}
            {viewLeaveModal && selectedLeave && (
                <div className="leave-modal-overlay" onClick={closeModal}>
                    <div className="leave-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="leave-modal-header">
                            <h3>Leave Details</h3>
                            <button className="close-btn" onClick={closeModal}>×</button>
                        </div>
                        <div className="leave-modal-body">
                            <p><strong>Employee:</strong> {selectedLeave.empName}</p>
                            <p><strong>Leave Type:</strong> {getLeaveTypeName(selectedLeave.leaveType)}</p>
                            <p><strong>Start Date:</strong> {moment(selectedLeave.startDate).format("DD/MM/YYYY")}</p>
                            <p><strong>End Date:</strong> {moment(selectedLeave.endDate).format("DD/MM/YYYY")}</p>

                            {/* Show half of the day if applicable */}
                            {(selectedLeave.halfOfTheDay == 2 || selectedLeave.halfOfTheDay == 1) && (
                                <p><strong>Half Day:</strong> {selectedLeave.halfOfTheDay == 1 ? "First Half" : selectedLeave.halfOfTheDay == 2 ? "Second Half" : ""}</p>
                            )}

                            {/* Show from/to times for short leave (type 12) */}
                            {selectedLeave.leaveType === 12 && selectedLeave.from > 0 && selectedLeave.to > 0 && (
                                <p><strong>Time:</strong> {moment.utc(selectedLeave.from * 1000).format("HH:mm")} - {moment.utc(selectedLeave.to * 1000).format("HH:mm")}</p>
                            )}

                            {/* Show leave message/reason */}
                            {selectedLeave.leaveMsg && (
                                <p>
                                    <strong>Reason:</strong> {stripHtmlTags(selectedLeave.leaveMsg)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProjectLeaveCalendarView;
